import React, { useCallback, useEffect, useRef, useState } from 'react';
import { UncontrolledReactSVGPanZoom } from 'react-svg-pan-zoom';
import { Button } from './Button';

interface SvgViewerProps {
  url: string;
  altText?: string;
  width?: number;
  height?: number;
  className?: string;
  svgWidth?: number;
  svgHeight?: number;
  exhibits?: Array<{
    id: string;
    position: { x: number; y: number };
    title?: string;
    onClick?: () => void;
  }>;
}

export const SvgViewer: React.FC<SvgViewerProps> = ({
  url,
  altText = 'SVG Map',
  width = 800,
  height = 600,
  className = '',
  svgWidth = 1395.57,
  svgHeight = 1083.06,
  exhibits = []
}) => {
  const [svgContent, setSvgContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actualSvgWidth, setActualSvgWidth] = useState(svgWidth);
  const [actualSvgHeight, setActualSvgHeight] = useState(svgHeight);
  const viewerRef = useRef<any>(null);

  // Exhibit marker styling
  const markerRadius = 10;
  const markerColor = "#ef4444"; // red-500
  const markerStrokeColor = "#dc2626"; // red-600

  // Function to extract SVG dimensions from content
  const extractSvgDimensions = (svgText: string): { width: number; height: number } => {
    try {
      // Try to extract viewBox first
      const viewBoxMatch = svgText.match(/viewBox=["']([^"']+)["']/);
      if (viewBoxMatch) {
        const [, viewBox] = viewBoxMatch;
        const [, , width, height] = viewBox.split(' ').map(Number);
        return { width, height };
      }
      
      // Try to extract width and height attributes
      const widthMatch = svgText.match(/width=["']([^"']+)["']/);
      const heightMatch = svgText.match(/height=["']([^"']+)["']/);
      
      if (widthMatch && heightMatch) {
        return {
          width: parseFloat(widthMatch[1]),
          height: parseFloat(heightMatch[1])
        };
      }
      
      // Fallback to default values
      return { width: svgWidth, height: svgHeight };
    } catch (err) {
      console.error('Error extracting SVG dimensions:', err);
      return { width: svgWidth, height: svgHeight };
    }
  };

  // Function to extract inner content from SVG
  const extractSvgContent = (svgText: string): string => {
    try {
      // Remove the outer <svg> tag and keep only the inner content
      const innerContent = svgText.replace(/<svg[^>]*>/, '').replace(/<\/svg>$/, '');
      return innerContent.trim();
    } catch (err) {
      console.error('Error parsing SVG content:', err);
      return svgText; // Return original if parsing fails
    }
  };

  // Calculate dimensions based on actual SVG size
  const calculatedWidth = actualSvgWidth / actualSvgHeight * height;
  const scaleFactorMin = Math.min(height / actualSvgHeight, calculatedWidth / actualSvgWidth);

  // Load SVG content from URL
  React.useEffect(() => {
    const loadSvg = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Accept': 'image/svg+xml, text/plain, */*',
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to load SVG: ${response.statusText}`);
        }
        
        const svgText = await response.text();
        
        // Extract dimensions from the SVG content
        const dimensions = extractSvgDimensions(svgText);
        setActualSvgWidth(dimensions.width);
        setActualSvgHeight(dimensions.height);
        
        // Extract inner content (remove outer svg tag)
        const innerContent = extractSvgContent(svgText);
        setSvgContent(innerContent);
      } catch (err) {
        console.error('Error loading SVG:', err);
        setError(err instanceof Error ? err.message : 'Failed to load SVG');
      } finally {
        setIsLoading(false);
      }
    };

    if (url) {
      loadSvg();
    }
  }, [url]);

  // Fit to viewer after SVG loads - exactly like your example
  useEffect(() => {
    setTimeout(() => {
      viewerRef.current?.fitToViewer("center", "center");
    }, 100);
  }, [svgContent]);

  const handleZoomIn = useCallback(() => {
    if (viewerRef.current) {
      viewerRef.current.zoomOnViewerCenter(1.2);
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (viewerRef.current) {
      viewerRef.current.zoomOnViewerCenter(0.8);
    }
  }, []);

  const handleReset = useCallback(() => {
    if (viewerRef.current) {
    //   viewerRef.current.fitToViewer("center", "center");
    viewerRef.current.fitToViewer(0, 0, actualSvgWidth, actualSvgHeight);
    }
  }, []);

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ width: calculatedWidth, height }}>
        <div className="text-gray-500">Loading SVG...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ width: calculatedWidth, height }}>
        <div className="text-red-500 text-center">
          <p>Error loading SVG</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!svgContent) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ width: calculatedWidth, height }}>
        <div className="text-gray-500">No SVG content available</div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`} style={{ display: "flex", justifyContent: "center" }}>
      {/* Toolbar */}
      <div className="flex gap-2 mb-4 p-2 bg-gray-100 rounded-lg">
        <Button
          variant="outline"
          size="sm"
          onPress={handleZoomIn}
        >
          Zoom In
        </Button>
        <Button
          variant="outline"
          size="sm"
          onPress={handleZoomOut}
        >
          Zoom Out
        </Button>
        <Button
          variant="outline"
          size="sm"
          onPress={handleReset}
        >
          Reset
        </Button>
      </div>

      {/* SVG Viewer with Border */}
      <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-100 shadow-lg flex items-center justify-center h-full p-2">
        <UncontrolledReactSVGPanZoom
          ref={viewerRef}
          detectAutoPan={false}
          detectWheel={true}
          preventPanOutside={true}
          width={calculatedWidth}
          height={height}
          background="#ffff"
          miniatureProps={{ 
            position: "none",
            background: "#ffff",
            width: 100,
            height: 100
          }}
          scaleFactorMin={scaleFactorMin}
          scaleFactorMax={10}
          onZoom={currentValue => {
            console.log('onZoom', currentValue);
          }}
          onPan={currentValue => {
            console.log('onPan', currentValue);
            
            // Type assertion since we know the actual structure from console log
            const value = currentValue as any;
            
            // Calculate scaled SVG dimensions using the currentValue passed to onPan
            const scaledSvgWidth = actualSvgWidth * value.a;
            const scaledSvgHeight = actualSvgHeight * value.d;
            
            // Calculate boundaries
            const maxPanX = Math.max(0, scaledSvgWidth - calculatedWidth);
            const maxPanY = Math.max(0, scaledSvgHeight - height);
            
            // Prevent panning outside boundaries
            const constrainedE = Math.max(-maxPanX, Math.min(0, value.e));
            const constrainedF = Math.max(-maxPanY, Math.min(0, value.f));
            
            // Update the value with constrained pan values
            if (constrainedE !== value.e || constrainedF !== value.f) {
              const newValue = {
                ...value,
                e: constrainedE,
                f: constrainedF
              };
              viewerRef.current?.changeValue(newValue);
            }
          }}
          onClick={event => {
            console.log(`x: ${event.x}, y: ${event.y}`);
          }}
          
        >
          <svg viewBox={`0 0 ${actualSvgWidth} ${actualSvgHeight}`}>
            <g dangerouslySetInnerHTML={{ __html: svgContent }} />
            
            {/* Exhibit Markers */}
            {exhibits.map((exhibit, index) => (
              <g key={exhibit.id} onClick={exhibit.onClick} style={{ cursor: exhibit.onClick ? 'pointer' : 'default' }}>
                {/* Marker circle */}
                <circle
                  cx={exhibit.position.x}
                  cy={exhibit.position.y}
                  r={markerRadius}
                  fill={markerColor}
                  stroke={markerStrokeColor}
                  strokeWidth="2"
                />
                
                {/* Marker number */}
                <text
                  x={exhibit.position.x}
                  y={exhibit.position.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="white"
                  fontSize="12"
                  fontWeight="bold"
                >
                  {index + 1}
                </text>
                
                {/* Title tooltip (optional) */}
                {exhibit.title && (
                  <title>{exhibit.title}</title>
                )}
              </g>
            ))}
          </svg>
        </UncontrolledReactSVGPanZoom>
      </div>

      {/* Instructions */}
      <div className="mt-2 text-xs text-gray-500">
        <p>• Drag to pan • Scroll to zoom • Use buttons to reset</p>
      </div>

      {/* Alt text */}
      {altText && (
        <p className="text-sm text-gray-500 mt-2">{altText}</p>
      )}
    </div>
  );
};

export default SvgViewer; 